import { useState, useRef } from 'react';
import { Helmet } from 'react-helmet-async';
import { Link } from 'react-router-dom';
import {
  ArrowRight,
  ChefHat,
  Plus,
  Trash2,
  RotateCcw,
  Calculator,
  CheckCircle2,
  Printer,
  BookOpen,
  AlertTriangle,
  List,
  Utensils,
} from 'lucide-react';

/* ─── Types ──────────────────────────────────────────── */

interface IngredientRow {
  id: number;
  nombre: string;
  cantidad: string;
  unidad: string;
  precioBase: string; // precio por kg/L/ud
  merma: string; // porcentaje de merma
  pesoGramos: string; // peso bruto en gramos (optional)
}

const UNITS = ['g', 'kg', 'ml', 'L', 'ud'];

let nextId = 20;

/* ─── Example: Paella Valenciana ─────────────────────── */

const PAELLA_EXAMPLE: IngredientRow[] = [
  { id: 1, nombre: 'Arroz bomba', cantidad: '80', unidad: 'g', precioBase: '2.80', merma: '0', pesoGramos: '80' },
  { id: 2, nombre: 'Pollo troceado', cantidad: '120', unidad: 'g', precioBase: '5.90', merma: '15', pesoGramos: '120' },
  { id: 3, nombre: 'Judía verde ferradura', cantidad: '30', unidad: 'g', precioBase: '3.20', merma: '10', pesoGramos: '30' },
  { id: 4, nombre: 'Garrofón', cantidad: '20', unidad: 'g', precioBase: '12.50', merma: '0', pesoGramos: '20' },
  { id: 5, nombre: 'Tomate rallado', cantidad: '40', unidad: 'g', precioBase: '2.10', merma: '5', pesoGramos: '40' },
  { id: 6, nombre: 'Aceite de oliva virgen', cantidad: '15', unidad: 'ml', precioBase: '8.50', merma: '0', pesoGramos: '14' },
  { id: 7, nombre: 'Azafrán', cantidad: '0.15', unidad: 'g', precioBase: '4500', merma: '0', pesoGramos: '0.15' },
  { id: 8, nombre: 'Caldo de pollo', cantidad: '200', unidad: 'ml', precioBase: '1.80', merma: '5', pesoGramos: '200' },
  { id: 9, nombre: 'Sal', cantidad: '2', unidad: 'g', precioBase: '0.30', merma: '0', pesoGramos: '2' },
];

/* ─── FAQs ───────────────────────────────────────────── */

const faqs = [
  {
    q: '¿Qué es un escandallo de cocina?',
    a: 'Un escandallo (o ficha de coste) es el desglose detallado de todos los ingredientes de un plato con sus cantidades, precios, mermas y coste resultante. Es la herramienta fundamental para conocer el coste real de producción de cada receta y fijar precios de venta rentables.',
  },
  {
    q: '¿Cómo se calcula el escandallo de un plato?',
    a: 'Se listan todos los ingredientes con su cantidad bruta, se aplica el porcentaje de merma de cada uno (limpieza, cocción, etc.), se multiplica la cantidad resultante por el precio unitario de compra, y se suman todos los costes. El total dividido entre el número de raciones da el coste por ración.',
  },
  {
    q: '¿Qué porcentaje de food cost es normal?',
    a: 'El food cost óptimo en hostelería está entre el 25% y el 35%. Por debajo del 30% es excelente, entre 30-35% es aceptable y por encima del 35% indica que hay que revisar precios o proveedores. El rango varía según el tipo de establecimiento.',
  },
  {
    q: '¿Qué es la merma y cómo afecta al escandallo?',
    a: 'La merma es la pérdida de peso o volumen de un ingrediente al limpiarlo, pelarlo, despinarlo o cocinarlo. Un kilo de gambas con 40% de merma solo rinde 600 g utilizables, lo que encarece el coste real de la materia prima. No contabilizar mermas es el error más común al hacer escandallos.',
  },
  {
    q: '¿Cada cuánto hay que actualizar los escandallos?',
    a: 'Cada vez que cambien los precios de compra. Los productos frescos pueden variar hasta un 20% en una semana. Lo ideal es un software que actualice automáticamente todos los escandallos cuando se registra una nueva factura. Con PLEKO, esto ocurre en tiempo real.',
  },
  {
    q: '¿Cuál es la diferencia entre escandallo y ficha técnica?',
    a: 'El escandallo se centra en el desglose económico: ingredientes, cantidades, mermas y costes. La ficha técnica incluye el escandallo más la elaboración paso a paso, foto del emplatado, alérgenos, información nutricional y tiempo de preparación.',
  },
  {
    q: '¿Cómo calculo el precio de venta a partir del escandallo?',
    a: 'La fórmula es: PVP = Coste del plato / Food cost objetivo. Si tu coste es 4,50 € y quieres un food cost del 30%, el PVP mínimo es 4,50 / 0,30 = 15 €. A partir de ahí, ajustas según el posicionamiento y la competencia.',
  },
  {
    q: '¿Esta calculadora es gratuita?',
    a: 'Sí, esta calculadora de escandallos es 100% gratuita y no requiere registro. Para funcionalidades avanzadas como escandallos automáticos con OCR de facturas, precios de mercado en tiempo real y control de food cost para toda la carta, puedes probar PLEKO gratis durante 14 días.',
  },
];

/* ─── Schema.org JSON-LD ─────────────────────────────── */

const faqJsonLd = {
  '@context': 'https://schema.org',
  '@type': 'FAQPage',
  mainEntity: faqs.map((faq) => ({
    '@type': 'Question',
    name: faq.q,
    acceptedAnswer: { '@type': 'Answer', text: faq.a },
  })),
};

const webAppJsonLd = {
  '@context': 'https://schema.org',
  '@type': 'WebApplication',
  name: 'Calculadora de Escandallo Online Gratis',
  url: 'https://pleko.app/calculadora-escandallo',
  applicationCategory: 'BusinessApplication',
  operatingSystem: 'All',
  offers: {
    '@type': 'Offer',
    price: '0',
    priceCurrency: 'EUR',
  },
  author: { '@type': 'Organization', name: 'PLEKO' },
  description:
    'Calculadora de escandallo gratuita para restaurantes. Calcula el coste de tus platos con mermas, food cost y precio de venta sugerido.',
};

const howToJsonLd = {
  '@context': 'https://schema.org',
  '@type': 'HowTo',
  name: 'Cómo calcular un escandallo con nuestra calculadora',
  description: 'Sigue estos 3 pasos para calcular el escandallo de cualquier plato de tu restaurante.',
  step: [
    {
      '@type': 'HowToStep',
      position: 1,
      name: 'Introduce los ingredientes',
      text: 'Añade cada ingrediente con su nombre, cantidad por ración, unidad, precio de compra por kilo/litro y porcentaje de merma.',
    },
    {
      '@type': 'HowToStep',
      position: 2,
      name: 'Configura las raciones y el precio de venta',
      text: 'Indica cuántas raciones produce la receta y el precio de venta del plato (PVP) para calcular el food cost.',
    },
    {
      '@type': 'HowToStep',
      position: 3,
      name: 'Analiza los resultados',
      text: 'Revisa el coste total, coste por ración, food cost % con semáforo, precio de venta sugerido, peso por ración y coste por kg.',
    },
  ],
};

const breadcrumbJsonLd = {
  '@context': 'https://schema.org',
  '@type': 'BreadcrumbList',
  itemListElement: [
    { '@type': 'ListItem', position: 1, name: 'PLEKO', item: 'https://pleko.app/' },
    { '@type': 'ListItem', position: 2, name: 'Escandallo', item: 'https://pleko.app/escandallo' },
    {
      '@type': 'ListItem',
      position: 3,
      name: 'Calculadora de Escandallo',
      item: 'https://pleko.app/calculadora-escandallo',
    },
  ],
};

/* ─── Helpers ────────────────────────────────────────── */

function createEmptyRow(): IngredientRow {
  return { id: nextId++, nombre: '', cantidad: '', unidad: 'g', precioBase: '', merma: '0', pesoGramos: '' };
}

function p(v: string): number {
  return parseFloat(v.replace(',', '.'));
}

function fmt(n: number, decimals = 2): string {
  return n.toFixed(decimals).replace('.', ',');
}

/* ─── Table of Contents ──────────────────────────────── */

const toc = [
  { id: 'calculadora', label: 'Calculadora de escandallos' },
  { id: 'como-usar', label: 'Cómo usar la calculadora' },
  { id: 'que-es-escandallo', label: '¿Qué es un escandallo?' },
  { id: 'formulas', label: 'Fórmulas del escandallo' },
  { id: 'tabla-mermas', label: 'Tabla de mermas de referencia' },
  { id: 'errores-comunes', label: 'Errores comunes al calcular escandallos' },
  { id: 'excel-vs-online', label: 'Excel vs calculadora online vs software' },
  { id: 'faqs', label: 'Preguntas frecuentes' },
];

/* ─── Component ──────────────────────────────────────── */

export default function CalculadoraEscandalloPage() {
  const [ingredients, setIngredients] = useState<IngredientRow[]>([
    createEmptyRow(),
    createEmptyRow(),
    createEmptyRow(),
  ]);
  const [raciones, setRaciones] = useState('1');
  const [precioVenta, setPrecioVenta] = useState('');
  const printRef = useRef<HTMLDivElement>(null);

  /* ── Ingredient CRUD ── */
  const updateIngredient = (id: number, field: keyof IngredientRow, value: string) => {
    setIngredients((prev) => prev.map((ing) => (ing.id === id ? { ...ing, [field]: value } : ing)));
  };

  const addRow = () => setIngredients((prev) => [...prev, createEmptyRow()]);

  const removeRow = (id: number) => {
    if (ingredients.length <= 1) return;
    setIngredients((prev) => prev.filter((ing) => ing.id !== id));
  };

  const reset = () => {
    nextId = 20;
    setIngredients([createEmptyRow(), createEmptyRow(), createEmptyRow()]);
    setRaciones('1');
    setPrecioVenta('');
  };

  const loadExample = () => {
    setIngredients(PAELLA_EXAMPLE.map((ing) => ({ ...ing })));
    setRaciones('4');
    setPrecioVenta('14.50');
  };

  /* ── Calculations ── */
  const getRowCost = (ing: IngredientRow): number => {
    const cantidad = p(ing.cantidad);
    const precio = p(ing.precioBase);
    const merma = p(ing.merma) || 0;
    if (isNaN(cantidad) || isNaN(precio) || cantidad <= 0) return 0;

    // Convert to base units (kg or L) for price multiplication
    let factor = 1;
    if (ing.unidad === 'g') factor = 0.001;
    if (ing.unidad === 'ml') factor = 0.001;

    // Coste = cantidad_base * precio * (1 + merma/100)
    return cantidad * factor * precio * (1 + merma / 100);
  };

  const getRowWeight = (ing: IngredientRow): number => {
    const w = p(ing.pesoGramos);
    return isNaN(w) ? 0 : w;
  };

  const costeTotalReceta = ingredients.reduce((sum, ing) => sum + getRowCost(ing), 0);
  const numRaciones = Math.max(1, Math.round(p(raciones)) || 1);
  const costePorRacion = costeTotalReceta / numRaciones;
  const pvp = p(precioVenta);
  const hasPvp = !isNaN(pvp) && pvp > 0;
  const foodCostPct = hasPvp ? (costePorRacion / pvp) * 100 : null;
  const pvpSugerido = costePorRacion > 0 ? costePorRacion / 0.3 : 0;
  const pesoTotalBruto = ingredients.reduce((sum, ing) => sum + getRowWeight(ing), 0);
  const pesoPorRacion = pesoTotalBruto > 0 ? pesoTotalBruto / numRaciones : 0;
  const costePorKg = pesoTotalBruto > 0 ? costeTotalReceta / (pesoTotalBruto / 1000) : 0;
  const margenBruto = hasPvp ? pvp - costePorRacion : null;

  const getTrafficLight = (pct: number) => {
    if (pct < 30) return { label: 'Excelente', bg: 'bg-green-50', border: 'border-green-200', text: 'text-green-700', dot: 'bg-green-500' };
    if (pct <= 35) return { label: 'Aceptable', bg: 'bg-yellow-50', border: 'border-yellow-200', text: 'text-yellow-700', dot: 'bg-yellow-500' };
    return { label: 'Revisar', bg: 'bg-red-50', border: 'border-red-200', text: 'text-red-700', dot: 'bg-red-500' };
  };

  const getPriceLabel = (unit: string) => {
    if (unit === 'g' || unit === 'kg') return '€/kg';
    if (unit === 'ml' || unit === 'L') return '€/L';
    return '€/ud';
  };

  const handlePrint = () => {
    window.print();
  };

  /* ── Render ── */
  return (
    <>
      <Helmet>
        <title>Calculadora de Escandallo Online Gratis [2026] | Calcula Costes al Instante | PLEKO</title>
        <meta
          name="description"
          content="Calculadora de escandallo online gratis para restaurantes. Calcula el coste de tus platos con mermas, food cost %, precio de venta sugerido, peso por ración y coste por kg. Sin registro."
        />
        <meta
          name="keywords"
          content="calculadora escandallo, calcular escandallo, calculadora food cost, calcular coste plato, escandallo online gratis, simulador escandallo, escandallo restaurante, escandallo hostelería"
        />
        <link rel="canonical" href="https://pleko.app/calculadora-escandallo" />
        <meta property="og:type" content="website" />
        <meta property="og:url" content="https://pleko.app/calculadora-escandallo" />
        <meta
          property="og:title"
          content="Calculadora de Escandallo Online Gratis [2026] | Calcula Costes al Instante | PLEKO"
        />
        <meta
          property="og:description"
          content="Calcula el escandallo de tus platos gratis: ingredientes, mermas, food cost, precio de venta sugerido. Herramienta profesional para hostelería."
        />
        <meta property="og:image" content="https://pleko.app/chef-hero.jpg" />
        <meta property="og:locale" content="es_ES" />
        <meta name="twitter:card" content="summary_large_image" />
        <meta
          name="twitter:title"
          content="Calculadora de Escandallo Online Gratis [2026] | PLEKO"
        />
        <meta
          name="twitter:description"
          content="Calcula el escandallo de tus platos gratis: ingredientes, mermas, food cost, precio de venta sugerido."
        />
        <script type="application/ld+json">{JSON.stringify(faqJsonLd)}</script>
        <script type="application/ld+json">{JSON.stringify(webAppJsonLd)}</script>
        <script type="application/ld+json">{JSON.stringify(howToJsonLd)}</script>
        <script type="application/ld+json">{JSON.stringify(breadcrumbJsonLd)}</script>
      </Helmet>

      <div className="bg-white">
        {/* Hero */}
        <section className="bg-gradient-to-b from-blue-50 to-white py-16 lg:py-24">
          <div className="max-w-4xl mx-auto px-4 sm:px-6 lg:px-8">
            <div className="text-center">
              <span className="inline-flex items-center gap-1.5 px-3 py-1 bg-blue-100 text-blue-700 rounded-full text-sm font-medium mb-6">
                <Calculator className="w-4 h-4" />
                Herramienta gratuita — sin registro
              </span>
              <h1 className="text-4xl sm:text-5xl font-extrabold text-slate-900 mb-6 tracking-tight">
                Calculadora de <span className="text-blue-500">Escandallo</span> Online Gratis
              </h1>
              <p className="text-xl text-slate-600 mb-4 max-w-2xl mx-auto">
                Calcula el coste de tus platos al instante. Introduce ingredientes, mermas y precios para obtener el escandallo completo con food cost, precio de venta sugerido y coste por kg.
              </p>
              <p className="text-base text-slate-500 max-w-xl mx-auto">
                Incluye ejemplo precargado de Paella Valenciana con precios reales de mercado.
              </p>
            </div>
          </div>
        </section>

        {/* Table of Contents */}
        <section className="max-w-4xl mx-auto px-4 sm:px-6 lg:px-8 pt-8">
          <nav className="bg-slate-50 border border-slate-200 rounded-xl p-6 mb-12">
            <div className="flex items-center gap-2 mb-4">
              <List className="w-5 h-5 text-blue-500" />
              <h2 className="text-lg font-bold text-slate-900">Contenido de esta pagina</h2>
            </div>
            <ol className="grid sm:grid-cols-2 gap-x-8 gap-y-2">
              {toc.map((item, i) => (
                <li key={item.id}>
                  <a href={`#${item.id}`} className="text-sm text-blue-600 hover:text-blue-800 hover:underline">
                    {i + 1}. {item.label}
                  </a>
                </li>
              ))}
            </ol>
          </nav>
        </section>

        {/* ═══════════ CALCULATOR ═══════════ */}
        <section id="calculadora" className="max-w-4xl mx-auto px-4 sm:px-6 lg:px-8 pb-16 scroll-mt-20">
          <div ref={printRef} className="border border-slate-200 rounded-2xl p-6 sm:p-8 mb-8 print:border-0 print:p-0">
            <div className="flex flex-col sm:flex-row sm:items-center sm:justify-between gap-4 mb-6">
              <h2 className="text-2xl font-bold text-slate-900">Escandallo del plato</h2>
              <div className="flex gap-2 print:hidden">
                <button
                  onClick={loadExample}
                  className="inline-flex items-center gap-1.5 px-4 py-2 text-sm font-medium text-blue-600 bg-blue-50 rounded-lg hover:bg-blue-100 transition-colors"
                >
                  <Utensils className="w-4 h-4" />
                  Cargar ejemplo
                </button>
                <button
                  onClick={reset}
                  className="inline-flex items-center gap-1.5 px-4 py-2 text-sm font-medium text-slate-600 bg-slate-50 rounded-lg hover:bg-slate-100 transition-colors"
                >
                  <RotateCcw className="w-4 h-4" />
                  Limpiar todo
                </button>
              </div>
            </div>

            {/* Header row - hidden on mobile */}
            <div className="hidden sm:grid sm:grid-cols-[1fr_80px_70px_90px_70px_80px_36px] gap-2 mb-2">
              <span className="text-xs font-semibold text-slate-500 uppercase tracking-wider">Ingrediente</span>
              <span className="text-xs font-semibold text-slate-500 uppercase tracking-wider">Cantidad</span>
              <span className="text-xs font-semibold text-slate-500 uppercase tracking-wider">Unidad</span>
              <span className="text-xs font-semibold text-slate-500 uppercase tracking-wider">Precio</span>
              <span className="text-xs font-semibold text-slate-500 uppercase tracking-wider">Merma %</span>
              <span className="text-xs font-semibold text-slate-500 uppercase tracking-wider text-right">Coste</span>
              <span />
            </div>

            {/* Ingredient rows */}
            <div className="space-y-2">
              {ingredients.map((ing) => {
                const rowCost = getRowCost(ing);
                return (
                  <div
                    key={ing.id}
                    className="grid grid-cols-2 sm:grid-cols-[1fr_80px_70px_90px_70px_80px_36px] gap-2 items-center bg-slate-50 sm:bg-transparent p-3 sm:p-0 rounded-xl sm:rounded-none"
                  >
                    <input
                      type="text"
                      placeholder="Ingrediente"
                      value={ing.nombre}
                      onChange={(e) => updateIngredient(ing.id, 'nombre', e.target.value)}
                      className="col-span-2 sm:col-span-1 w-full px-3 py-2 border border-slate-200 rounded-lg text-sm focus:outline-none focus:ring-2 focus:ring-blue-500 focus:border-transparent"
                    />
                    <input
                      type="text"
                      inputMode="decimal"
                      placeholder="Cant."
                      value={ing.cantidad}
                      onChange={(e) => updateIngredient(ing.id, 'cantidad', e.target.value)}
                      className="w-full px-2 py-2 border border-slate-200 rounded-lg text-sm focus:outline-none focus:ring-2 focus:ring-blue-500 focus:border-transparent"
                    />
                    <select
                      value={ing.unidad}
                      onChange={(e) => updateIngredient(ing.id, 'unidad', e.target.value)}
                      className="w-full px-1 py-2 border border-slate-200 rounded-lg text-sm focus:outline-none focus:ring-2 focus:ring-blue-500 focus:border-transparent bg-white"
                    >
                      {UNITS.map((u) => (
                        <option key={u} value={u}>
                          {u}
                        </option>
                      ))}
                    </select>
                    <input
                      type="text"
                      inputMode="decimal"
                      placeholder={getPriceLabel(ing.unidad)}
                      value={ing.precioBase}
                      onChange={(e) => updateIngredient(ing.id, 'precioBase', e.target.value)}
                      className="w-full px-2 py-2 border border-slate-200 rounded-lg text-sm focus:outline-none focus:ring-2 focus:ring-blue-500 focus:border-transparent"
                    />
                    <input
                      type="text"
                      inputMode="decimal"
                      placeholder="0"
                      value={ing.merma}
                      onChange={(e) => updateIngredient(ing.id, 'merma', e.target.value)}
                      className="w-full px-2 py-2 border border-slate-200 rounded-lg text-sm focus:outline-none focus:ring-2 focus:ring-blue-500 focus:border-transparent"
                    />
                    <span className="text-sm font-medium text-slate-700 text-right tabular-nums hidden sm:block">
                      {rowCost > 0 ? `${fmt(rowCost)}€` : '—'}
                    </span>
                    <button
                      onClick={() => removeRow(ing.id)}
                      className="p-1.5 text-slate-400 hover:text-red-500 transition-colors justify-self-center print:hidden"
                      title="Eliminar ingrediente"
                    >
                      <Trash2 className="w-4 h-4" />
                    </button>
                    {/* Mobile cost display */}
                    <div className="col-span-2 sm:hidden text-right text-sm font-medium text-slate-600">
                      {rowCost > 0 ? `Coste: ${fmt(rowCost)} €` : ''}
                    </div>
                  </div>
                );
              })}
            </div>

            {/* Add row button */}
            <div className="flex gap-3 mt-4 print:hidden">
              <button
                onClick={addRow}
                className="inline-flex items-center gap-1.5 px-4 py-2 text-sm font-medium text-blue-600 bg-blue-50 rounded-lg hover:bg-blue-100 transition-colors"
              >
                <Plus className="w-4 h-4" />
                Añadir ingrediente
              </button>
            </div>

            {/* Config: Raciones + PVP */}
            <div className="border-t border-slate-200 mt-6 pt-6">
              <div className="grid grid-cols-1 sm:grid-cols-3 gap-4">
                <div>
                  <label className="block text-sm font-medium text-slate-700 mb-1">Numero de raciones</label>
                  <input
                    type="text"
                    inputMode="numeric"
                    placeholder="1"
                    value={raciones}
                    onChange={(e) => setRaciones(e.target.value)}
                    className="w-full px-3 py-2 border border-slate-200 rounded-lg text-sm focus:outline-none focus:ring-2 focus:ring-blue-500 focus:border-transparent"
                  />
                </div>
                <div>
                  <label className="block text-sm font-medium text-slate-700 mb-1">Precio de venta (PVP)</label>
                  <div className="relative">
                    <input
                      type="text"
                      inputMode="decimal"
                      placeholder="0,00"
                      value={precioVenta}
                      onChange={(e) => setPrecioVenta(e.target.value)}
                      className="w-full px-3 py-2 border border-slate-200 rounded-lg text-sm focus:outline-none focus:ring-2 focus:ring-blue-500 focus:border-transparent pr-8"
                    />
                    <span className="absolute right-3 top-1/2 -translate-y-1/2 text-slate-400 text-sm">€</span>
                  </div>
                </div>
                <div className="flex items-end">
                  <button
                    onClick={handlePrint}
                    className="inline-flex items-center gap-1.5 px-4 py-2 text-sm font-medium text-slate-600 bg-slate-100 rounded-lg hover:bg-slate-200 transition-colors w-full justify-center print:hidden"
                  >
                    <Printer className="w-4 h-4" />
                    Imprimir escandallo
                  </button>
                </div>
              </div>
            </div>

            {/* ── Results ── */}
            {costeTotalReceta > 0 && (
              <div className="mt-8">
                <h3 className="text-lg font-bold text-slate-900 mb-4">Resultado del escandallo</h3>

                {/* Primary metrics */}
                <div className="grid grid-cols-2 lg:grid-cols-4 gap-3 mb-4">
                  <div className="bg-blue-50 border border-blue-200 rounded-xl p-4 text-center">
                    <p className="text-xs text-slate-500 mb-1 uppercase tracking-wider">Coste total</p>
                    <p className="text-2xl font-bold text-blue-600">{fmt(costeTotalReceta)} €</p>
                    {numRaciones > 1 && <p className="text-xs text-slate-400 mt-1">{numRaciones} raciones</p>}
                  </div>
                  <div className="bg-slate-50 border border-slate-200 rounded-xl p-4 text-center">
                    <p className="text-xs text-slate-500 mb-1 uppercase tracking-wider">Coste / ración</p>
                    <p className="text-2xl font-bold text-slate-800">{fmt(costePorRacion)} €</p>
                  </div>
                  {foodCostPct !== null && (
                    <div
                      className={`${getTrafficLight(foodCostPct).bg} border ${getTrafficLight(foodCostPct).border} rounded-xl p-4 text-center`}
                    >
                      <p className="text-xs text-slate-500 mb-1 uppercase tracking-wider">Food cost</p>
                      <div className="flex items-center justify-center gap-2">
                        <span className={`w-3 h-3 rounded-full ${getTrafficLight(foodCostPct).dot}`} />
                        <p className={`text-2xl font-bold ${getTrafficLight(foodCostPct).text}`}>
                          {fmt(foodCostPct, 1)}%
                        </p>
                      </div>
                      <p className={`text-xs mt-1 ${getTrafficLight(foodCostPct).text}`}>
                        {getTrafficLight(foodCostPct).label}
                      </p>
                    </div>
                  )}
                  <div className="bg-green-50 border border-green-200 rounded-xl p-4 text-center">
                    <p className="text-xs text-slate-500 mb-1 uppercase tracking-wider">PVP sugerido (30%)</p>
                    <p className="text-2xl font-bold text-green-600">{fmt(pvpSugerido)} €</p>
                  </div>
                </div>

                {/* Secondary metrics */}
                <div className="grid grid-cols-2 lg:grid-cols-4 gap-3">
                  {margenBruto !== null && (
                    <div className="bg-emerald-50 border border-emerald-200 rounded-xl p-3 text-center">
                      <p className="text-xs text-slate-500 mb-1">Margen bruto</p>
                      <p className="text-lg font-bold text-emerald-600">{fmt(margenBruto)} €</p>
                    </div>
                  )}
                  {pesoPorRacion > 0 && (
                    <div className="bg-slate-50 border border-slate-200 rounded-xl p-3 text-center">
                      <p className="text-xs text-slate-500 mb-1">Peso / ración</p>
                      <p className="text-lg font-bold text-slate-700">{fmt(pesoPorRacion, 0)} g</p>
                    </div>
                  )}
                  {costePorKg > 0 && (
                    <div className="bg-slate-50 border border-slate-200 rounded-xl p-3 text-center">
                      <p className="text-xs text-slate-500 mb-1">Coste / kg</p>
                      <p className="text-lg font-bold text-slate-700">{fmt(costePorKg)} €</p>
                    </div>
                  )}
                  {pesoTotalBruto > 0 && (
                    <div className="bg-slate-50 border border-slate-200 rounded-xl p-3 text-center">
                      <p className="text-xs text-slate-500 mb-1">Peso total bruto</p>
                      <p className="text-lg font-bold text-slate-700">{fmt(pesoTotalBruto, 0)} g</p>
                    </div>
                  )}
                </div>

                {/* Food cost alert */}
                {foodCostPct !== null && foodCostPct > 35 && (
                  <div className="mt-4 bg-red-50 border border-red-200 rounded-xl p-4 flex items-start gap-3">
                    <AlertTriangle className="w-5 h-5 text-red-500 flex-shrink-0 mt-0.5" />
                    <div>
                      <p className="font-semibold text-red-800">Food cost por encima del 35%</p>
                      <p className="text-sm text-red-700 mt-1">
                        Considera subir el PVP a al menos <strong>{fmt(pvpSugerido)} €</strong> para alcanzar un food cost del 30%, o busca ingredientes alternativos más economicos.
                      </p>
                    </div>
                  </div>
                )}
              </div>
            )}
          </div>

          {/* CTA after calculator */}
          <div className="bg-slate-50 border border-slate-200 rounded-2xl p-6 mb-12 print:hidden">
            <div className="flex flex-col sm:flex-row items-center gap-4">
              <div className="flex-1">
                <p className="font-bold text-slate-900 mb-1">
                  Esta calculadora es una version simplificada
                </p>
                <p className="text-sm text-slate-600">
                  Con PLEKO, calcula escandallos automaticos con OCR de facturas, precios de mercado en tiempo real,
                  sub-recetas con coste en cascada y control de food cost para toda tu carta.
                </p>
              </div>
              <Link
                to="/register"
                className="inline-flex items-center gap-2 px-6 py-3 bg-blue-500 text-white font-semibold rounded-xl hover:bg-blue-600 transition-colors shadow-lg shadow-blue-200 flex-shrink-0"
              >
                Probar PLEKO gratis
                <ArrowRight className="w-4 h-4" />
              </Link>
            </div>
          </div>

          {/* ═══════════ SEO CONTENT ═══════════ */}
          <div className="prose prose-lg max-w-none">

            {/* 1. Cómo usar */}
            <h2 id="como-usar" className="text-3xl font-bold text-slate-900 mb-6 scroll-mt-20">
              Como usar nuestra calculadora de escandallos
            </h2>
            <p className="text-slate-700 text-lg leading-relaxed mb-6">
              Calcular el escandallo de un plato con nuestra herramienta es rapido y gratuito. Sigue estos tres pasos:
            </p>
            <div className="space-y-4 mb-8">
              {[
                {
                  step: '1',
                  title: 'Introduce los ingredientes',
                  desc: 'Añade cada ingrediente de la receta con su nombre, cantidad por ración (en gramos, kg, ml, litros o unidades), el precio de compra por kilo o litro, y el porcentaje de merma. Si no conoces la merma exacta, consulta nuestra tabla de referencia más abajo. Puedes pulsar "Cargar ejemplo" para ver una Paella Valenciana precargada con datos reales.',
                },
                {
                  step: '2',
                  title: 'Configura raciones y precio de venta',
                  desc: 'Indica cuántas raciones produce tu receta. Si ya tienes un precio de venta (PVP), introdúcelo para calcular el food cost real. Si no, la calculadora te sugerirá el PVP mínimo para un food cost del 30%.',
                },
                {
                  step: '3',
                  title: 'Analiza los resultados',
                  desc: 'Revisa el coste total, coste por ración, food cost con semáforo (verde/amarillo/rojo), precio de venta sugerido, peso por ración y coste por kilo. Si el food cost supera el 35%, recibirás una alerta con recomendaciones.',
                },
              ].map(({ step, title, desc }) => (
                <div key={step} className="flex gap-4 p-4 bg-slate-50 rounded-xl border border-slate-200">
                  <span className="flex-shrink-0 w-8 h-8 rounded-full bg-blue-500 text-white font-bold flex items-center justify-center text-sm">
                    {step}
                  </span>
                  <div>
                    <p className="font-semibold text-slate-800">{title}</p>
                    <p className="text-slate-600 text-sm mt-1">{desc}</p>
                  </div>
                </div>
              ))}
            </div>

            {/* 2. ¿Qué es un escandallo? */}
            <h2 id="que-es-escandallo" className="text-3xl font-bold text-slate-900 mb-6 scroll-mt-20">
              ¿Que es un escandallo y por que necesitas calcularlo?
            </h2>
            <p className="text-slate-700 text-lg leading-relaxed mb-6">
              Un <strong>escandallo</strong> — también llamado <strong>ficha de coste</strong>, <strong>hoja de escandallo</strong> o <strong>escandallo de cocina</strong> — es el documento que desglosa el coste exacto de elaborar un plato. Incluye cada ingrediente con su cantidad bruta, el porcentaje de merma (lo que se pierde al limpiar, pelar o cocinar), la cantidad neta resultante y el precio de compra de cada componente.
            </p>
            <p className="text-slate-700 text-lg leading-relaxed mb-6">
              El resultado final es el <strong>coste de producción del plato</strong>, que comparado con el precio de venta al público (PVP) nos da el <strong>food cost</strong>: la métrica clave de rentabilidad en hostelería. Sin este cálculo, estás fijando precios a ciegas.
            </p>
            <p className="text-slate-700 text-lg leading-relaxed mb-6">
              Según datos del sector, el <strong>60% de los restaurantes que cierran en los primeros 3 años no llevaban un control de costes adecuado</strong>. El escandallo es la primera línea de defensa contra la pérdida de rentabilidad silenciosa: esa subida del 10% en el precio del aceite de oliva que, sin escandallo actualizado, nadie detecta hasta que la cuenta de resultados ya está en rojo.
            </p>
            <p className="text-slate-700 text-lg leading-relaxed mb-6">
              Un escandallo bien hecho te permite:
            </p>
            <ul className="space-y-2 mb-8">
              {[
                'Conocer el coste real de cada plato de tu carta, no una estimación',
                'Fijar precios de venta con margen garantizado',
                'Detectar inmediatamente el impacto de subidas de precios de proveedores',
                'Comparar la rentabilidad entre platos y tomar decisiones de carta basadas en datos',
                'Negociar mejor con proveedores, sabiendo exactamente cuánto consumes de cada ingrediente',
                'Calcular el food cost global de tu restaurante y compararlo con el objetivo',
                'Planificar menús del día y menús degustación con un margen definido',
              ].map((item) => (
                <li key={item} className="flex items-start gap-3 text-slate-700">
                  <CheckCircle2 className="w-5 h-5 text-green-500 flex-shrink-0 mt-0.5" />
                  <span>{item}</span>
                </li>
              ))}
            </ul>
            <p className="text-slate-700 text-lg leading-relaxed mb-8">
              El escandallo se aplica a todo tipo de establecimientos: <strong>restaurantes, bares, cafeterías, pastelerías, caterings, hoteles, comedores colectivos y dark kitchens</strong>. Cualquier negocio que transforme materias primas en productos para el consumidor final necesita escandallar sus recetas.
            </p>

            {/* 3. Fórmulas */}
            <h2 id="formulas" className="text-3xl font-bold text-slate-900 mb-6 scroll-mt-20">
              Formulas del escandallo: las matematicas detras del calculo
            </h2>
            <p className="text-slate-700 text-lg leading-relaxed mb-6">
              Aunque nuestra calculadora de arriba hace todos los calculos automaticamente, es importante entender las formulas que hay detras. Aqui tienes las cuatro formulas fundamentales del escandallo:
            </p>

            {/* Formula 1 */}
            <div className="bg-blue-50 border border-blue-100 rounded-xl p-6 mb-6">
              <p className="text-blue-800 font-semibold mb-2">1. Coste total de ingredientes</p>
              <p className="text-center text-xl font-bold text-blue-600 font-mono">
                Coste total = &Sigma; (precio_unitario &times; cantidad &times; (1 + merma%))
              </p>
              <p className="text-sm text-slate-600 mt-3">
                <strong>Ejemplo:</strong> 120 g de pollo a 5,90 €/kg con 15% de merma por huesos = 0,120 &times; 5,90 &times; 1,15 = <strong>0,81 €</strong>. La merma encarece el ingrediente porque compras 120 g pero solo aprovechas 102 g.
              </p>
            </div>

            {/* Formula 2 */}
            <div className="bg-green-50 border border-green-100 rounded-xl p-6 mb-6">
              <p className="text-green-800 font-semibold mb-2">2. Food Cost (%)</p>
              <p className="text-center text-xl font-bold text-green-600 font-mono">
                Food Cost (%) = (Coste del plato / PVP) &times; 100
              </p>
              <p className="text-sm text-slate-600 mt-3">
                <strong>Ejemplo:</strong> Plato que cuesta 4,50 € en ingredientes y vendes a 15 € &rarr; Food Cost = (4,50 / 15) &times; 100 = <strong>30%</strong>. Eso significa que de cada euro que ingresa el restaurante por ese plato, 30 centimos se destinan a materia prima.
              </p>
            </div>

            {/* Formula 3 */}
            <div className="bg-amber-50 border border-amber-100 rounded-xl p-6 mb-6">
              <p className="text-amber-800 font-semibold mb-2">3. Precio de venta sugerido (PVP)</p>
              <p className="text-center text-xl font-bold text-amber-600 font-mono">
                PVP sugerido = Coste del plato / 0,30
              </p>
              <p className="text-sm text-slate-600 mt-3">
                <strong>Ejemplo:</strong> Si el coste por racion es 4,50 €, el PVP minimo para mantener un food cost del 30% es 4,50 / 0,30 = <strong>15 €</strong>. A partir de ahi, ajustas segun posicionamiento, competencia y valor percibido.
              </p>
            </div>

            {/* Formula 4 */}
            <div className="bg-purple-50 border border-purple-100 rounded-xl p-6 mb-8">
              <p className="text-purple-800 font-semibold mb-2">4. Coste por kg</p>
              <p className="text-center text-xl font-bold text-purple-600 font-mono">
                Coste/kg = Coste total / (Peso total en kg)
              </p>
              <p className="text-sm text-slate-600 mt-3">
                <strong>Ejemplo:</strong> Receta de 4 raciones con coste total de 18 € y peso total de 2 kg &rarr; Coste/kg = 18 / 2 = <strong>9 €/kg</strong>. Esta metrica es esencial para comparar la rentabilidad entre recetas de distinto gramaje.
              </p>
            </div>

            {/* 4. Tabla de mermas */}
            <h2 id="tabla-mermas" className="text-3xl font-bold text-slate-900 mb-6 scroll-mt-20">
              Tabla de mermas de referencia para tu escandallo
            </h2>
            <p className="text-slate-700 text-lg leading-relaxed mb-6">
              La <strong>merma</strong> es la diferencia entre el peso bruto de compra y el peso neto que aprovechas. Es el factor mas olvidado — y mas costoso — del escandallo. Un kilo de gambas a 22 € con un 40% de merma te cuesta realmente <strong>36,67 € por kilo utilizable</strong>. Aqui tienes una tabla profesional de referencia:
            </p>

            {/* Carnes */}
            <h3 className="text-xl font-bold text-slate-800 mb-3 mt-8">Carnes</h3>
            <div className="overflow-x-auto mb-6">
              <table className="w-full border-collapse text-sm">
                <thead>
                  <tr className="bg-slate-800 text-white">
                    <th className="p-3 text-left font-medium">Producto</th>
                    <th className="p-3 text-right font-medium">Merma (%)</th>
                    <th className="p-3 text-left font-medium">Motivo principal</th>
                  </tr>
                </thead>
                <tbody>
                  {[
                    ['Pollo entero', '25-30%', 'Huesos, piel, menudillos'],
                    ['Ternera (pieza entera)', '15-20%', 'Grasa, nervios, recortes'],
                    ['Cerdo (lomo, solomillo)', '10-15%', 'Grasa externa, tendones'],
                    ['Cordero (pierna, costillar)', '25-35%', 'Hueso, grasa, recortes'],
                    ['Pato entero', '30-40%', 'Huesos, grasa subcutanea abundante'],
                    ['Conejo', '25-30%', 'Huesos, visceras, cabeza'],
                  ].map(([prod, merma, motivo], i) => (
                    <tr key={i} className={i % 2 === 0 ? 'bg-white' : 'bg-slate-50'}>
                      <td className="p-3 border-b border-slate-100 font-medium text-slate-800">{prod}</td>
                      <td className="p-3 border-b border-slate-100 text-right font-semibold text-amber-600">{merma}</td>
                      <td className="p-3 border-b border-slate-100 text-slate-600">{motivo}</td>
                    </tr>
                  ))}
                </tbody>
              </table>
            </div>

            {/* Pescados */}
            <h3 className="text-xl font-bold text-slate-800 mb-3">Pescados</h3>
            <div className="overflow-x-auto mb-6">
              <table className="w-full border-collapse text-sm">
                <thead>
                  <tr className="bg-slate-800 text-white">
                    <th className="p-3 text-left font-medium">Producto</th>
                    <th className="p-3 text-right font-medium">Merma (%)</th>
                    <th className="p-3 text-left font-medium">Motivo principal</th>
                  </tr>
                </thead>
                <tbody>
                  {[
                    ['Merluza entera', '45-50%', 'Cabeza, espinas, visceras, piel'],
                    ['Salmon (filete)', '15-20%', 'Piel, espinas, grasa'],
                    ['Rape entero', '50-55%', 'Cabeza enorme (50% del peso), piel'],
                    ['Lubina entera', '40-45%', 'Escamas, espinas, visceras, cabeza'],
                    ['Dorada entera', '40-45%', 'Escamas, visceras, espinas'],
                    ['Bacalao (lomo)', '10-15%', 'Espinas, piel'],
                  ].map(([prod, merma, motivo], i) => (
                    <tr key={i} className={i % 2 === 0 ? 'bg-white' : 'bg-slate-50'}>
                      <td className="p-3 border-b border-slate-100 font-medium text-slate-800">{prod}</td>
                      <td className="p-3 border-b border-slate-100 text-right font-semibold text-amber-600">{merma}</td>
                      <td className="p-3 border-b border-slate-100 text-slate-600">{motivo}</td>
                    </tr>
                  ))}
                </tbody>
              </table>
            </div>

            {/* Mariscos */}
            <h3 className="text-xl font-bold text-slate-800 mb-3">Mariscos</h3>
            <div className="overflow-x-auto mb-6">
              <table className="w-full border-collapse text-sm">
                <thead>
                  <tr className="bg-slate-800 text-white">
                    <th className="p-3 text-left font-medium">Producto</th>
                    <th className="p-3 text-right font-medium">Merma (%)</th>
                    <th className="p-3 text-left font-medium">Motivo principal</th>
                  </tr>
                </thead>
                <tbody>
                  {[
                    ['Gambas / langostinos', '35-40%', 'Cabeza, cascara, intestino'],
                    ['Mejillones', '60-65%', 'Conchas (peso muy elevado)'],
                    ['Almejas', '55-60%', 'Conchas, arena, ejemplares cerrados'],
                    ['Cigalas', '40-50%', 'Cabeza, cascara, pinzas'],
                    ['Pulpo', '30-35%', 'Coccion (pierde agua y peso)'],
                  ].map(([prod, merma, motivo], i) => (
                    <tr key={i} className={i % 2 === 0 ? 'bg-white' : 'bg-slate-50'}>
                      <td className="p-3 border-b border-slate-100 font-medium text-slate-800">{prod}</td>
                      <td className="p-3 border-b border-slate-100 text-right font-semibold text-amber-600">{merma}</td>
                      <td className="p-3 border-b border-slate-100 text-slate-600">{motivo}</td>
                    </tr>
                  ))}
                </tbody>
              </table>
            </div>

            {/* Verduras y frutas */}
            <h3 className="text-xl font-bold text-slate-800 mb-3">Verduras y frutas</h3>
            <div className="overflow-x-auto mb-6">
              <table className="w-full border-collapse text-sm">
                <thead>
                  <tr className="bg-slate-800 text-white">
                    <th className="p-3 text-left font-medium">Producto</th>
                    <th className="p-3 text-right font-medium">Merma (%)</th>
                    <th className="p-3 text-left font-medium">Motivo principal</th>
                  </tr>
                </thead>
                <tbody>
                  {[
                    ['Alcachofa', '60-65%', 'Hojas externas, tallo, pelusa interior'],
                    ['Patata', '15-20%', 'Piel, ojos, zonas verdes'],
                    ['Cebolla', '10%', 'Piel seca, raiz'],
                    ['Pimiento', '15-20%', 'Semillas, nervios, pedunculo'],
                    ['Tomate', '5-10%', 'Piel (si se pela), pedunculo'],
                    ['Piña', '45-50%', 'Piel gruesa, corazon, corona'],
                    ['Naranja', '30-35%', 'Piel, pepitas, membranas'],
                    ['Manzana', '10-15%', 'Piel (si se pela), corazon'],
                  ].map(([prod, merma, motivo], i) => (
                    <tr key={i} className={i % 2 === 0 ? 'bg-white' : 'bg-slate-50'}>
                      <td className="p-3 border-b border-slate-100 font-medium text-slate-800">{prod}</td>
                      <td className="p-3 border-b border-slate-100 text-right font-semibold text-amber-600">{merma}</td>
                      <td className="p-3 border-b border-slate-100 text-slate-600">{motivo}</td>
                    </tr>
                  ))}
                </tbody>
              </table>
            </div>

            {/* Setas */}
            <h3 className="text-xl font-bold text-slate-800 mb-3">Setas</h3>
            <div className="overflow-x-auto mb-8">
              <table className="w-full border-collapse text-sm">
                <thead>
                  <tr className="bg-slate-800 text-white">
                    <th className="p-3 text-left font-medium">Producto</th>
                    <th className="p-3 text-right font-medium">Merma (%)</th>
                    <th className="p-3 text-left font-medium">Motivo principal</th>
                  </tr>
                </thead>
                <tbody>
                  {[
                    ['Boletus (ceps)', '10-15%', 'Tierra, base del pie, zonas blandas'],
                    ['Champiñon', '5-10%', 'Base del pie, tierra'],
                    ['Rebozuelo (rossinyol)', '10-15%', 'Tierra, partes secas'],
                  ].map(([prod, merma, motivo], i) => (
                    <tr key={i} className={i % 2 === 0 ? 'bg-white' : 'bg-slate-50'}>
                      <td className="p-3 border-b border-slate-100 font-medium text-slate-800">{prod}</td>
                      <td className="p-3 border-b border-slate-100 text-right font-semibold text-amber-600">{merma}</td>
                      <td className="p-3 border-b border-slate-100 text-slate-600">{motivo}</td>
                    </tr>
                  ))}
                </tbody>
              </table>
            </div>

            <p className="text-slate-700 text-lg leading-relaxed mb-8">
              Estas mermas son orientativas para producto fresco de calidad media. La merma real depende de la calidad del proveedor, la temporada y la habilidad del equipo de cocina. Lo ideal es que cada restaurante calcule sus mermas reales pesando antes y despues de la manipulacion. <Link to="/mermas-cocina" className="text-blue-600 hover:underline">Guia completa de mermas en cocina &rarr;</Link>
            </p>

            {/* 5. Errores comunes */}
            <h2 id="errores-comunes" className="text-3xl font-bold text-slate-900 mb-6 scroll-mt-20">
              5 errores comunes al calcular escandallos (y como evitarlos)
            </h2>

            <div className="space-y-6 mb-10">
              <div className="border-l-4 border-red-400 pl-5">
                <h3 className="text-lg font-bold text-slate-800 mb-2">1. No contabilizar las mermas</h3>
                <p className="text-slate-700 leading-relaxed">
                  Es el error numero uno. Si compras un kilo de gambas a 22 € y no aplicas el 40% de merma (cabezas, cascaras), tu escandallo dira que el kilo utilizable cuesta 22 € cuando en realidad cuesta <strong>36,67 €</strong>. Esa diferencia de 14,67 € se multiplica por cada plato que lleve gambas, cada servicio, cada dia. Al final del mes, pueden ser cientos de euros de beneficio invisible que nunca llegan.
                </p>
              </div>

              <div className="border-l-4 border-red-400 pl-5">
                <h3 className="text-lg font-bold text-slate-800 mb-2">2. Usar precios desactualizados</h3>
                <p className="text-slate-700 leading-relaxed">
                  Los precios de producto fresco fluctuan constantemente. El aceite de oliva ha subido un 40% en dos años. Si tu escandallo sigue con el precio de hace 6 meses, tus márgenes reales son mucho peores de lo que crees. <strong>Revisa los precios cada vez que recibas una factura</strong>, o mejor aun, usa un software que los actualice automaticamente.
                </p>
              </div>

              <div className="border-l-4 border-red-400 pl-5">
                <h3 className="text-lg font-bold text-slate-800 mb-2">3. Olvidar ingredientes "invisibles"</h3>
                <p className="text-slate-700 leading-relaxed">
                  Aceite de fritura, sal, pimienta, guarniciones, salsas base, mantequilla para sellar... Muchos cocineros no incluyen estos ingredientes en el escandallo porque "cuestan poco". Pero sumados, pueden representar entre un <strong>5% y un 15% del coste total del plato</strong>. En una carta de 40 platos, ese "poco" se convierte en miles de euros al año.
                </p>
              </div>

              <div className="border-l-4 border-red-400 pl-5">
                <h3 className="text-lg font-bold text-slate-800 mb-2">4. No distinguir entre coste de receta y coste por racion</h3>
                <p className="text-slate-700 leading-relaxed">
                  Una olla de caldo de 20 litros no es una racion. Si tu receta produce 8 raciones, el coste que importa es el <strong>coste por racion</strong>, no el coste total de la elaboracion. Parece obvio, pero muchos escandallos en Excel confunden ambos valores, especialmente en elaboraciones base (salsas, masas, caldos) que se usan en multiples recetas.
                </p>
              </div>

              <div className="border-l-4 border-red-400 pl-5">
                <h3 className="text-lg font-bold text-slate-800 mb-2">5. Calcular el escandallo una vez y no volver a tocarlo</h3>
                <p className="text-slate-700 leading-relaxed">
                  Un escandallo no es un documento estatico. Los precios cambian, los proveedores cambian, las porciones se descontrolan con el tiempo. Un escandallo que se calculo hace un año es <strong>un escandallo que miente</strong>. Lo ideal es actualizarlo cada vez que cambie un precio de compra. Con software como PLEKO, esto ocurre automaticamente cada vez que escaneas un albaran.
                </p>
              </div>
            </div>

            {/* 6. Excel vs online vs software */}
            <h2 id="excel-vs-online" className="text-3xl font-bold text-slate-900 mb-6 scroll-mt-20">
              Escandallo en Excel vs calculadora online vs software profesional
            </h2>
            <p className="text-slate-700 text-lg leading-relaxed mb-6">
              Hay tres formas principales de calcular escandallos. Cada una tiene sus ventajas y limitaciones:
            </p>

            <div className="grid md:grid-cols-3 gap-4 mb-8">
              {/* Excel */}
              <div className="border border-slate-200 rounded-xl p-5">
                <h3 className="font-bold text-slate-700 mb-3 flex items-center gap-2">
                  <span className="text-lg">📋</span> Excel / Google Sheets
                </h3>
                <ul className="space-y-2 text-sm text-slate-600 mb-4">
                  <li className="flex gap-2"><CheckCircle2 className="w-4 h-4 text-green-500 flex-shrink-0 mt-0.5" />Gratis y flexible</li>
                  <li className="flex gap-2"><CheckCircle2 className="w-4 h-4 text-green-500 flex-shrink-0 mt-0.5" />Personalizable al maximo</li>
                  <li className="flex gap-2"><span className="text-red-400">✗</span>Actualizacion manual de precios</li>
                  <li className="flex gap-2"><span className="text-red-400">✗</span>Errores de formulas frecuentes</li>
                  <li className="flex gap-2"><span className="text-red-400">✗</span>No escala con +50 recetas</li>
                  <li className="flex gap-2"><span className="text-red-400">✗</span>Imposible compartir en equipo</li>
                </ul>
                <p className="text-xs text-slate-500 italic">Recomendado: restaurantes muy pequeños (1-10 recetas).</p>
              </div>

              {/* Calculadora online */}
              <div className="border border-slate-200 rounded-xl p-5">
                <h3 className="font-bold text-slate-700 mb-3 flex items-center gap-2">
                  <span className="text-lg">🌐</span> Calculadora online (esta pagina)
                </h3>
                <ul className="space-y-2 text-sm text-slate-600 mb-4">
                  <li className="flex gap-2"><CheckCircle2 className="w-4 h-4 text-green-500 flex-shrink-0 mt-0.5" />Gratis y sin registro</li>
                  <li className="flex gap-2"><CheckCircle2 className="w-4 h-4 text-green-500 flex-shrink-0 mt-0.5" />Calculo instantaneo con mermas</li>
                  <li className="flex gap-2"><CheckCircle2 className="w-4 h-4 text-green-500 flex-shrink-0 mt-0.5" />Semaforo de food cost</li>
                  <li className="flex gap-2"><span className="text-red-400">✗</span>No guarda datos entre sesiones</li>
                  <li className="flex gap-2"><span className="text-red-400">✗</span>Sin historial de precios</li>
                  <li className="flex gap-2"><span className="text-red-400">✗</span>Sin sub-recetas ni cascada</li>
                </ul>
                <p className="text-xs text-slate-500 italic">Recomendado: calculos puntuales, pruebas rapidas, aprendizaje.</p>
              </div>

              {/* Software profesional */}
              <div className="border-2 border-blue-300 bg-blue-50 rounded-xl p-5">
                <h3 className="font-bold text-blue-700 mb-3 flex items-center gap-2">
                  <span className="text-lg">⚡</span> Software profesional (PLEKO)
                </h3>
                <ul className="space-y-2 text-sm text-slate-700 mb-4">
                  <li className="flex gap-2"><CheckCircle2 className="w-4 h-4 text-green-500 flex-shrink-0 mt-0.5" />Actualizacion automatica de precios</li>
                  <li className="flex gap-2"><CheckCircle2 className="w-4 h-4 text-green-500 flex-shrink-0 mt-0.5" />OCR: foto al albaran y listo</li>
                  <li className="flex gap-2"><CheckCircle2 className="w-4 h-4 text-green-500 flex-shrink-0 mt-0.5" />Sub-recetas con coste en cascada</li>
                  <li className="flex gap-2"><CheckCircle2 className="w-4 h-4 text-green-500 flex-shrink-0 mt-0.5" />Alertas de food cost en tiempo real</li>
                  <li className="flex gap-2"><CheckCircle2 className="w-4 h-4 text-green-500 flex-shrink-0 mt-0.5" />Equipo conectado desde cualquier dispositivo</li>
                  <li className="flex gap-2"><CheckCircle2 className="w-4 h-4 text-green-500 flex-shrink-0 mt-0.5" />Historial de precios con graficos</li>
                  <li className="flex gap-2"><CheckCircle2 className="w-4 h-4 text-green-500 flex-shrink-0 mt-0.5" />Precios de mercado mayorista en tiempo real</li>
                  <li className="flex gap-2"><CheckCircle2 className="w-4 h-4 text-green-500 flex-shrink-0 mt-0.5" />Mermas sugeridas por categoria</li>
                </ul>
                <p className="text-xs text-blue-600 font-medium">Recomendado: cualquier restaurante que quiera controlar costes en serio.</p>
              </div>
            </div>

            <p className="text-slate-700 text-lg leading-relaxed mb-6">
              La <strong>calculadora de esta pagina</strong> es perfecta para entender como funciona un escandallo, hacer un calculo puntual o convencer a tu socio de que necesitais controlar costes. Pero si gestionas un restaurante con mas de 20 recetas, necesitas un sistema que se actualice solo.
            </p>
            <p className="text-slate-700 text-lg leading-relaxed mb-8">
              Con PLEKO, <strong>escaneas el albaran con el movil y todos los escandallos se actualizan automaticamente</strong>. Sin copiar numeros a mano, sin errores de formula, sin perder horas cada semana. <Link to="/plantilla-escandallo-excel" className="text-blue-600 hover:underline">Si prefieres empezar con Excel, descarga nuestra plantilla gratuita &rarr;</Link>
            </p>

            <p className="text-slate-700 text-lg leading-relaxed mb-6">
              Ademas de escandallos, un software profesional como PLEKO te ofrece funcionalidades que una hoja de calculo nunca podra replicar:
            </p>
            <ul className="space-y-2 mb-8">
              {[
                'Ingenieria de menu: clasifica tus platos en Stars, Plowhorses, Puzzles y Dogs segun rentabilidad y popularidad',
                'Carta digital con QR integrada con los escandallos para sugerir PVP automaticamente',
                'Comparacion de precios con mercados mayoristas (Mercabarna, Mercamadrid) en tiempo real',
                'Optimizador de recetas con IA: sugiere cambios de ingredientes para bajar el food cost sin perder calidad',
                'Control horario de empleados integrado (cumplimiento RD-ley 8/2019)',
                'Pedidos a proveedores basados en el consumo real de tus recetas',
                'Informes de rentabilidad por plato, categoria y periodo',
              ].map((item) => (
                <li key={item} className="flex items-start gap-3 text-slate-700">
                  <CheckCircle2 className="w-5 h-5 text-blue-500 flex-shrink-0 mt-0.5" />
                  <span>{item}</span>
                </li>
              ))}
            </ul>
          </div>

          {/* FAQ */}
          <div id="faqs" className="mt-16 scroll-mt-20">
            <h2 className="text-3xl font-bold text-slate-900 mb-8 text-center">Preguntas frecuentes sobre escandallos</h2>
            <div className="space-y-4">
              {faqs.map(({ q, a }) => (
                <details key={q} className="border border-slate-200 rounded-xl overflow-hidden">
                  <summary className="p-5 font-semibold text-slate-800 cursor-pointer hover:bg-slate-50 flex justify-between items-center list-none">
                    {q}
                    <span className="text-slate-400 text-xl ml-4">+</span>
                  </summary>
                  <div className="px-5 pb-5 text-slate-600 leading-relaxed border-t border-slate-100 pt-4">{a}</div>
                </details>
              ))}
            </div>
          </div>

          {/* Internal links */}
          <div className="mt-12 p-6 bg-slate-50 rounded-xl border border-slate-200">
            <h3 className="font-bold text-slate-900 mb-3 flex items-center gap-2">
              <BookOpen className="w-5 h-5 text-blue-500" /> Guias relacionadas
            </h3>
            <div className="grid sm:grid-cols-2 gap-3">
              {[
                { to: '/escandallo', label: 'Que es un escandallo: guia completa' },
                { to: '/como-hacer-escandallo', label: 'Como hacer un escandallo paso a paso' },
                { to: '/ejemplo-escandallo', label: '8 ejemplos reales de escandallos' },
                { to: '/escandallo-bebidas', label: 'Escandallo de bebidas y cocteles' },
                { to: '/software-escandallo', label: 'El mejor software de escandallos' },
                { to: '/plantilla-escandallo-excel', label: 'Plantilla de escandallo en Excel (gratis)' },
                { to: '/food-cost', label: 'Food Cost: que es y como calcularlo' },
                { to: '/calculadora-food-cost', label: 'Calculadora de Food Cost gratuita' },
              ].map(({ to, label }) => (
                <Link key={to} to={to} className="flex items-center gap-2 text-sm text-blue-600 hover:text-blue-800 hover:underline">
                  <ArrowRight className="w-3 h-3" /> {label}
                </Link>
              ))}
            </div>
          </div>

          {/* Final CTA */}
          <div className="mt-16 bg-gradient-to-r from-blue-500 to-blue-600 rounded-2xl p-8 text-center text-white print:hidden">
            <ChefHat className="w-12 h-12 mx-auto mb-4 opacity-90" />
            <h2 className="text-2xl font-bold mb-3">¿Quieres guardar tus escandallos y que se actualicen solos?</h2>
            <p className="text-blue-100 mb-6 max-w-xl mx-auto">
              PLEKO calcula automaticamente el coste de todas tus recetas, controla mermas, escanea facturas con IA y te avisa cuando el food cost se dispara. Prueba gratis 14 dias.
            </p>
            <Link
              to="/register"
              className="inline-flex items-center gap-2 px-8 py-3 bg-white text-blue-600 font-bold rounded-xl hover:bg-blue-50 transition-colors"
            >
              Empezar gratis
              <ArrowRight className="w-4 h-4" />
            </Link>
            <p className="mt-3 text-blue-200 text-sm">Sin tarjeta de credito. Configurado en 2 minutos.</p>
          </div>
        </section>

        {/* Sticky bottom CTA bar */}
        <div className="fixed bottom-0 inset-x-0 bg-white/95 backdrop-blur border-t border-slate-200 py-3 px-4 z-50 print:hidden">
          <div className="max-w-4xl mx-auto flex items-center justify-between gap-4">
            <p className="text-sm text-slate-600 hidden sm:block">
              ¿Quieres guardar tus escandallos? <strong>Prueba PLEKO gratis 14 dias</strong>
            </p>
            <p className="text-sm text-slate-600 sm:hidden">
              <strong>Prueba PLEKO gratis</strong>
            </p>
            <Link
              to="/register"
              className="inline-flex items-center gap-2 px-5 py-2 bg-blue-500 text-white font-semibold rounded-lg hover:bg-blue-600 transition-colors text-sm flex-shrink-0"
            >
              Probar gratis
              <ArrowRight className="w-3.5 h-3.5" />
            </Link>
          </div>
        </div>
      </div>
    </>
  );
}
